import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, Eye, EyeOff, Package, Sparkles, DollarSign, Percent, Save, Loader2, Camera, Trash2, FileText, X, AlertTriangle, Wand2, Star, Link as LinkIcon, ShieldCheck, ShieldAlert, ShieldQuestion, PenSquare, Check, Tag } from "lucide-react";
import { toast } from "sonner";
import Autocomplete from "@/components/Autocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared grid-template-columns for the catalog table header AND every row.
// Kept as a plain string (not Tailwind arbitrary class) so both call sites
// resolve to the exact same computed value — Tailwind's JIT can otherwise
// diverge on values containing commas like `minmax(0,1fr)`.
// The last column is fixed (not `auto`) because grid tracks are computed per
// element; the header's "Actions" text is narrower than a row's 8 icons, which
// would otherwise cause `auto` to resolve differently in each row and pull the
// interior columns out of alignment.
const CATALOG_GRID = "32px 64px minmax(0,1fr) 150px 180px 100px 260px";

/**
 * Admin catalog manager for SinaLite products.
 * - Sync catalog from SinaLite (178 products)
 * - Filter by category / search
 * - Toggle product visibility on the storefront
 * - Set markup rules (percent / fixed) per product
 * - Refresh options for a product
 */
export default function AdminCatalog({ password }) {
  const headers = useMemo(() => ({ "X-Admin-Password": password }), [password]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all"); // all | visible | hidden
  const [photoFilter, setPhotoFilter] = useState("all");           // all | custom | placeholder
  const [contentFilter, setContentFilter] = useState("all");       // all | custom | default
  const [reviewFilter, setReviewFilter] = useState("all");         // all | ok | unsure | flagged | none
  const [editingContent, setEditingContent] = useState(null);       // product being edited
  const [selectedIds, setSelectedIds] = useState(() => new Set());  // bulk selection
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMarkupOpen, setBulkMarkupOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/sinalite/admin/products`, { headers }),
        axios.get(`${API}/sinalite/admin/categories`, { headers }),
      ]);
      setProducts(pRes.data.items || []);
      setCategories(cRes.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/sync-catalog`, {}, { headers });
      toast.success(`Synced ${data.total} products (${data.inserted} new, ${data.updated} updated)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Scraper — auto-fill missing photos & descriptions from SinaLite public pages.
  const [scrapeJob, setScrapeJob] = useState(null); // {id, status, scanned, total, matched, photos, descriptions}

  const startBulkScrape = async () => {
    if (!window.confirm("Auto-fill missing product photos and descriptions from SinaLite? Products SinaLite doesn't cover will fall back to Google Images. Existing custom content will NOT be overwritten.")) return;
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/scrape-sitemap`, { only_missing: true }, { headers });
      if (!data.ok && data.already_running) {
        toast.message("A scrape is already running — showing progress.");
        setScrapeJob({ id: data.job_id });
        return;
      }
      toast.success("Scrape started — this takes ~2 minutes for 130 products.");
      setScrapeJob({ id: data.job_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start scrape");
    }
  };

  // Poll job status while running.
  useEffect(() => {
    if (!scrapeJob?.id) return;
    let cancelled = false;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/sinalite/admin/scrape-jobs/${scrapeJob.id}`, { headers });
        if (cancelled) return;
        setScrapeJob(data);
        if (data.status === "done") {
          const gp = data.google_photos ?? 0;
          const googleNote = gp > 0 ? ` (${gp} via Google)` : "";
          toast.success(
            `Enriched ${data.matched} products — ${data.photos + gp} photos${googleNote} + ${data.descriptions} descriptions.`,
          );
          await load();
          stopped = true;
        } else if (data.status === "failed") {
          toast.error(`Scrape failed: ${data.error || "unknown"}`);
          stopped = true;
        }
      } catch { /* silent — will retry */ }
    };
    poll();
    const t = setInterval(() => { if (stopped) clearInterval(t); else poll(); }, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [scrapeJob?.id, headers, load]);

  const scrapeOne = async (id) => {
    // Async pattern — start the job, poll until done (matches bulk scrape).
    // Returns a promise that resolves when the job settles so callers (e.g.
    // ProductRow) can toggle their local spinner correctly.
    let jobId;
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/products/${id}/scrape-sinalite`, {}, { headers });
      jobId = data.job_id;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start scrape");
      return;
    }
    const started = Date.now();
    // Poll every 2s, cap at 3 min to prevent runaway UIs on truly stuck jobs.
    // Backend timeouts are shorter — a real scrape should finish inside 90s.
    while (Date.now() - started < 180000) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await axios.get(`${API}/sinalite/admin/scrape-single/${jobId}`, { headers });
        if (data.status === "done") {
          const parts = [];
          if (data.changed?.description === "set") parts.push("description");
          if (data.changed?.photo === "set") parts.push(`photo (${data.changed.photo_source || "sinalite"})`);
          toast.success(parts.length ? `Filled ${parts.join(" + ")}.` : "Nothing to fill — already have content.");
          await load();
          return;
        }
        if (data.status === "failed") {
          toast.error(data.error || "Scrape failed");
          return;
        }
      } catch { /* silent — retry */ }
    }
    toast.message("Scrape is taking longer than expected — check back in a minute.");
  };

  // AI copy generator — generates SEO alt-text + short description
  const generateCopyOne = async (id) => {
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/products/${id}/generate-copy`, { overwrite_description: false }, { headers, timeout: 45000 });
      const parts = [];
      if (data.changed?.description === "set") parts.push("description");
      if (data.changed?.seo_alt_text === "set") parts.push("alt-text");
      toast.success(parts.length ? `Generated ${parts.join(" + ")}.` : "Nothing to generate — already have copy.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copy generation failed");
    }
  };

  const [copyJob, setCopyJob] = useState(null);
  const startBulkCopy = async () => {
    if (!window.confirm("Generate SEO alt-text + descriptions for all products missing copy? Uses Claude Haiku vision (~$0.002 per image).")) return;
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/generate-copy`, {
        only_missing_description: true,
        only_missing_alt: false,
        scope: "all",
        overwrite_description: false,
      }, { headers });
      if (data.already_running) {
        toast.message("Copy generation already running — showing progress.");
      } else {
        toast.success("Copy generation started.");
      }
      setCopyJob({ id: data.job_id, status: "queued" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start copy generation");
    }
  };

  useEffect(() => {
    if (!copyJob?.id) return;
    let cancelled = false;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/sinalite/admin/copy-jobs/${copyJob.id}`, { headers });
        if (cancelled) return;
        setCopyJob(data);
        if (data.status === "done") {
          toast.success(`Copy done — ${data.descriptions || 0} descriptions · ${data.alt_texts || 0} alt-texts · ${data.errors || 0} errors.`);
          await load();
          stopped = true;
        } else if (data.status === "failed") {
          toast.error(`Copy generation failed: ${data.error || "unknown"}`);
          stopped = true;
        }
      } catch { /* silent — retry */ }
    };
    poll();
    const t = setInterval(() => { if (stopped) clearInterval(t); else poll(); }, 3500);
    return () => { cancelled = true; clearInterval(t); };
  }, [copyJob?.id, headers, load]);

  // AI photo review — single product
  const reviewOne = async (id) => {
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/products/${id}/review-photo`, {}, { headers, timeout: 45000 });
      setProducts((ps) => ps.map((p) => (p.id === id ? {
        ...p,
        photo_review_status: data.photo_review_status,
        photo_review_reason: data.photo_review_reason,
        photo_review_confidence: data.photo_review_confidence,
        photo_review_at: data.photo_review_at,
      } : p)));
      const label = { ok: "OK", unsure: "Unsure", flagged: "Flagged" }[data.photo_review_status] || data.photo_review_status;
      toast.success(`AI verdict: ${label} — ${data.photo_review_reason}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI review failed");
    }
  };

  // AI photo review — bulk background job
  const [reviewJob, setReviewJob] = useState(null);
  const startBulkReview = async (scope = "all") => {
    const label = scope === "google" ? "Google-sourced" : "all";
    if (!window.confirm(`Run AI photo review across ${label} product photos? Uses Claude Haiku vision (~$0.001 per image).`)) return;
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/review-photos`, { only_unreviewed: true, scope }, { headers });
      if (data.already_running) {
        toast.message("A review is already running — showing progress.");
      } else {
        toast.success("AI photo review started.");
      }
      setReviewJob({ id: data.job_id, status: "queued" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start review");
    }
  };

  useEffect(() => {
    if (!reviewJob?.id) return;
    let cancelled = false;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/sinalite/admin/review-jobs/${reviewJob.id}`, { headers });
        if (cancelled) return;
        setReviewJob(data);
        if (data.status === "done") {
          toast.success(`Review done — ${data.ok || 0} OK · ${data.unsure || 0} unsure · ${data.flagged || 0} flagged.`);
          await load();
          stopped = true;
        } else if (data.status === "failed") {
          toast.error(`Review failed: ${data.error || "unknown"}`);
          stopped = true;
        }
      } catch { /* silent — will retry */ }
    };
    poll();
    const t = setInterval(() => { if (stopped) clearInterval(t); else poll(); }, 3500);
    return () => { cancelled = true; clearInterval(t); };
  }, [reviewJob?.id, headers, load]);

  const toggleHomeFeatured = async (id, next) => {
    try {
      await axios.patch(`${API}/sinalite/admin/products/${id}/home-featured`, { featured: next }, { headers });
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, home_featured: next } : p)));
      toast.success(next ? "Featured on Home page." : "Removed from Home page.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not toggle featured");
    }
  };

  const saveScrapeUrl = async (id, url) => {
    try {
      await axios.patch(`${API}/sinalite/admin/products/${id}/scrape-url`, { url }, { headers });
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, scrape_url_override: url || null } : p)));
      toast.success(url ? "Manual scrape URL saved." : "Override cleared.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save URL");
    }
  };

  const toggleVisibility = async (id, next) => {
    const prev = products;
    setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, visible: next } : p)));
    try {
      await axios.patch(`${API}/sinalite/admin/products/${id}/visibility`, { visible: next }, { headers });
      toast.success(next ? "Product enabled on shop" : "Product hidden from shop");
    } catch (err) {
      setProducts(prev);
      toast.error("Failed to update visibility");
    }
  };

  const saveMarkup = async (id, markupType, markupValue) => {
    try {
      const { data } = await axios.patch(
        `${API}/sinalite/admin/products/${id}/markup`,
        { markup_type: markupType, markup_value: Number(markupValue) },
        { headers }
      );
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, ...data } : p)));
      toast.success(`Markup saved: ${markupType === "percent" ? `${markupValue}%` : `$${markupValue}`}`);
    } catch (err) {
      toast.error("Failed to save markup");
    }
  };

  const refreshOptions = async (id) => {
    try {
      const { data } = await axios.post(`${API}/sinalite/admin/products/${id}/refresh-options`, {}, { headers });
      toast.success(`Options refreshed (${(data.groups || []).length} groups)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh options");
    }
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG, PNG or WEBP images are accepted.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/sinalite/admin/products/${id}/photo`,
        form,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, has_owner_photo: true, owner_photo_url: data.url } : p)));
      toast.success("Product photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed.");
    }
  };

  const removePhoto = async (id) => {
    if (!window.confirm("Remove the custom photo for this product?")) return;
    try {
      await axios.delete(`${API}/sinalite/admin/products/${id}/photo`, { headers });
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, has_owner_photo: false, owner_photo_url: null } : p)));
      toast.success("Custom photo removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove photo.");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (selectedCategory !== "all" && p.category !== selectedCategory) return false;
      if (visibilityFilter === "visible" && !p.visible) return false;
      if (visibilityFilter === "hidden" && p.visible) return false;
      if (photoFilter === "custom" && !p.has_owner_photo) return false;
      if (photoFilter === "placeholder" && p.has_owner_photo) return false;
      if (contentFilter === "custom" && !((p.owner_description || "").trim())) return false;
      if (contentFilter === "default" && (p.owner_description || "").trim()) return false;
      if (reviewFilter !== "all") {
        const s = p.photo_review_status || "none";
        if (reviewFilter === "none" && s !== "none") return false;
        if (reviewFilter !== "none" && s !== reviewFilter) return false;
      }
      if (!q) return true;
      return (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q);
    });
  }, [products, search, selectedCategory, visibilityFilter, photoFilter, contentFilter, reviewFilter]);

  const stats = useMemo(() => ({
    total: products.length,
    visible: products.filter((p) => p.visible).length,
    hidden: products.filter((p) => !p.visible).length,
    categories: categories.length,
    placeholderCount: products.filter((p) => p.visible && !p.has_owner_photo).length,
    defaultDescCount: products.filter((p) => p.visible && !(p.owner_description || "").trim()).length,
    flaggedCount: products.filter((p) => p.photo_review_status === "flagged").length,
    unsureCount: products.filter((p) => p.photo_review_status === "unsure").length,
  }), [products, categories]);

  const saveContent = async (id, updates) => {
    try {
      const { data } = await axios.patch(`${API}/sinalite/admin/products/${id}/content`, updates, { headers });
      setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, owner_tagline: data.owner_tagline, owner_description: data.owner_description } : p)));
      toast.success("Description updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save description.");
      throw err;
    }
  };

  // --- Bulk selection helpers ---
  const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected = filteredIds.some((id) => selectedIds.has(id));

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (action, value, { confirmMsg, successMsg } = {}) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/sinalite/admin/products/bulk`,
        { product_ids: ids, action, value },
        { headers },
      );
      toast.success(successMsg ? `${successMsg} — ${data.modified} updated.` : `Updated ${data.modified} product${data.modified === 1 ? "" : "s"}.`);
      if (action === "delete") clearSelection();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const selectionCount = selectedIds.size;

  return (
    <div data-testid="admin-catalog">
      {/* Header row */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow gold-bar">Print Shop</div>
          <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">Catalog manager</h2>
          <p className="text-white/50 text-sm mt-2">
            {stats.total} products · {stats.visible} live · {stats.hidden} hidden · {stats.categories} categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="catalog-refresh-btn"
            className="btn-ghost !py-3 !px-4"
          ><RefreshCw size={14} /> Reload</button>
          <button
            onClick={startBulkScrape}
            disabled={scrapeJob?.status === "running" || scrapeJob?.status === "queued"}
            data-testid="catalog-scrape-btn"
            title="Fetch missing photos and descriptions from SinaLite — falls back to Google Images for products SinaLite doesn't cover"
            className="btn-ghost !py-3 !px-4 !text-[11px] border-purple-hi/50 text-purple-hi hover:bg-purple-hi hover:text-black transition-colors disabled:opacity-50"
          >
            <Wand2 size={14} />
            {scrapeJob?.status === "running" || scrapeJob?.status === "queued"
              ? (scrapeJob.phase === "google"
                  ? `Google ${scrapeJob.google_scanned ?? 0}/${scrapeJob.google_total ?? "…"}`
                  : `SinaLite ${scrapeJob.scanned ?? 0}/${scrapeJob.total ?? "…"}`)
              : "Auto-fill photos & copy"}
          </button>
          <button
            onClick={startBulkCopy}
            disabled={copyJob?.status === "running" || copyJob?.status === "queued"}
            data-testid="catalog-copy-btn"
            title="Generate SEO alt-text + short descriptions for every product missing copy — uses Claude Haiku vision"
            className="btn-ghost !py-3 !px-4 !text-[11px] border-emerald-400/40 text-emerald-300 hover:bg-emerald-300 hover:text-black transition-colors disabled:opacity-50"
          >
            <PenSquare size={14} />
            {copyJob?.status === "running" || copyJob?.status === "queued"
              ? `Writing ${copyJob.scanned ?? 0}/${copyJob.total ?? "…"}`
              : "Generate copy"}
          </button>
          <button
            onClick={() => startBulkReview("all")}
            disabled={reviewJob?.status === "running" || reviewJob?.status === "queued"}
            data-testid="catalog-review-btn"
            title="Run AI vision review across product photos — flags obvious mismatches for manual attention"
            className="btn-ghost !py-3 !px-4 !text-[11px] border-cyan-400/40 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors disabled:opacity-50"
          >
            <ShieldCheck size={14} />
            {reviewJob?.status === "running" || reviewJob?.status === "queued"
              ? `Reviewing ${reviewJob.scanned ?? 0}/${reviewJob.total ?? "…"}`
              : "AI photo review"}
          </button>
          <button
            onClick={runSync}
            disabled={syncing}
            data-testid="catalog-sync-btn"
            className="btn-gold !py-3 !px-5 !text-[11px] disabled:opacity-50"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {syncing ? "Syncing…" : "Sync from SinaLite"}
          </button>
        </div>
      </div>

      {/* Scrape job progress banner */}
      {scrapeJob && (scrapeJob.status === "running" || scrapeJob.status === "queued") && (
        <div className="card-dark border-purple-hi/40 bg-purple/5 p-4 mb-4 flex items-center gap-4" data-testid="scrape-progress">
          <Loader2 size={16} className="animate-spin text-purple-hi shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-white">
              {scrapeJob.phase === "google"
                ? `Google Images fallback — ${scrapeJob.google_scanned ?? 0} of ${scrapeJob.google_total ?? "…"} products`
                : `Scraping SinaLite — ${scrapeJob.scanned ?? 0} of ${scrapeJob.total ?? "…"} pages scanned`}
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">
              Matched {scrapeJob.matched ?? 0} · {scrapeJob.photos ?? 0} SinaLite photos · {scrapeJob.google_photos ?? 0} Google photos · {scrapeJob.descriptions ?? 0} descriptions
            </div>
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-hi transition-all duration-500"
                style={{
                  width: `${
                    scrapeJob.phase === "google"
                      ? Math.min(100, ((scrapeJob.google_scanned || 0) / Math.max(1, scrapeJob.google_total || 1)) * 100)
                      : Math.min(100, ((scrapeJob.scanned || 0) / Math.max(1, scrapeJob.total || 1)) * 100)
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Copy generation progress banner */}
      {copyJob && (copyJob.status === "running" || copyJob.status === "queued") && (
        <div className="card-dark border-emerald-400/40 bg-emerald-400/5 p-4 mb-4 flex items-center gap-4" data-testid="copy-progress">
          <Loader2 size={16} className="animate-spin text-emerald-300 shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-white">
              AI copy generation — {copyJob.scanned ?? 0} of {copyJob.total ?? "…"} products
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">
              {copyJob.descriptions ?? 0} descriptions · {copyJob.alt_texts ?? 0} alt-texts · {copyJob.errors ?? 0} errors
            </div>
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-300 transition-all duration-500"
                style={{ width: `${Math.min(100, ((copyJob.scanned || 0) / Math.max(1, copyJob.total || 1)) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Review job progress banner */}
      {reviewJob && (reviewJob.status === "running" || reviewJob.status === "queued") && (
        <div className="card-dark border-cyan-400/40 bg-cyan-400/5 p-4 mb-4 flex items-center gap-4" data-testid="review-progress">
          <Loader2 size={16} className="animate-spin text-cyan-300 shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-white">
              AI photo review — {reviewJob.scanned ?? 0} of {reviewJob.total ?? "…"} audited
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">
              {reviewJob.ok ?? 0} OK · {reviewJob.unsure ?? 0} unsure · {reviewJob.flagged ?? 0} flagged · {reviewJob.errors ?? 0} errors
            </div>
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-300 transition-all duration-500"
                style={{ width: `${Math.min(100, ((reviewJob.scanned || 0) / Math.max(1, reviewJob.total || 1)) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 mb-5">
        <Autocomplete
          variant="admin"
          testId="catalog-search"
          placeholder="Search by name, SKU or category…"
          value={search}
          onChange={setSearch}
          onQuery={async (q) => {
            const query = q.toLowerCase();
            return products
              .filter((p) => (p.name || "").toLowerCase().includes(query) || (p.sku || "").toLowerCase().includes(query) || (p.category || "").toLowerCase().includes(query))
              .slice(0, 8)
              .map((p) => ({
                type: "product",
                id: p.id,
                title: p.name,
                subtitle: `${p.category} · ${p.sku}`,
                image: p.owner_photo_url,
              }));
          }}
          onSelect={(item) => {
            setSearch(item.title);
            const el = document.querySelector(`[data-testid='catalog-row-${item.id}']`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        />
        <select
          data-testid="catalog-category-filter"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold min-w-[220px]"
        >
          <option value="all">All categories ({stats.categories})</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex border border-white/10 rounded-sm overflow-hidden text-[11px] uppercase tracking-[0.18em]">
          {["all", "visible", "hidden"].map((v) => (
            <button
              key={v}
              data-testid={`catalog-vis-${v}`}
              onClick={() => setVisibilityFilter(v)}
              className={`px-4 py-3 transition-colors ${visibilityFilter === v ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            >{v}</button>
          ))}
        </div>
      </div>

      {/* Secondary filters: photo status + description status */}
      <div className="flex flex-wrap items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.18em] text-white/50">
        <span>Filter:</span>
        <div className="flex border border-white/10 rounded-sm overflow-hidden">
          {[
            { key: "all", label: "All photos" },
            { key: "custom", label: "Custom" },
            { key: "placeholder", label: "Placeholder" },
          ].map((v) => (
            <button
              key={v.key}
              data-testid={`catalog-photo-${v.key}`}
              onClick={() => setPhotoFilter(v.key)}
              className={`px-3 py-2 transition-colors ${photoFilter === v.key ? "bg-gold text-black" : "text-white/55 hover:text-gold"}`}
            >{v.label}</button>
          ))}
        </div>
        <div className="flex border border-white/10 rounded-sm overflow-hidden">
          {[
            { key: "all", label: "All copy" },
            { key: "custom", label: "Custom" },
            { key: "default", label: "Auto/default" },
          ].map((v) => (
            <button
              key={v.key}
              data-testid={`catalog-content-${v.key}`}
              onClick={() => setContentFilter(v.key)}
              className={`px-3 py-2 transition-colors ${contentFilter === v.key ? "bg-gold text-black" : "text-white/55 hover:text-gold"}`}
            >{v.label}</button>
          ))}
        </div>
        <div className="flex border border-white/10 rounded-sm overflow-hidden">
          {[
            { key: "all", label: "All AI" },
            { key: "ok", label: "OK" },
            { key: "unsure", label: "Unsure" },
            { key: "flagged", label: "Flagged" },
            { key: "none", label: "Unreviewed" },
          ].map((v) => (
            <button
              key={v.key}
              data-testid={`catalog-review-${v.key}`}
              onClick={() => setReviewFilter(v.key)}
              className={`px-3 py-2 transition-colors ${reviewFilter === v.key ? "bg-cyan-300 text-black" : "text-white/55 hover:text-cyan-300"}`}
            >{v.label}</button>
          ))}
        </div>
        {(stats.placeholderCount > 0 || stats.defaultDescCount > 0 || stats.flaggedCount > 0) && (
          <div className="ml-auto flex items-center gap-2 text-[10px] text-white/50 normal-case">
            <AlertTriangle size={11} className="text-gold" />
            <span>
              {stats.placeholderCount > 0 && `${stats.placeholderCount} placeholder photo${stats.placeholderCount === 1 ? "" : "s"}`}
              {stats.defaultDescCount > 0 && `${stats.placeholderCount > 0 ? " · " : ""}${stats.defaultDescCount} auto description${stats.defaultDescCount === 1 ? "" : "s"}`}
              {stats.flaggedCount > 0 && `${(stats.placeholderCount + stats.defaultDescCount) > 0 ? " · " : ""}${stats.flaggedCount} AI-flagged photo${stats.flaggedCount === 1 ? "" : "s"}`}
              .
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="card-dark p-16 text-center text-white/50">Loading catalog…</div>
      ) : filtered.length === 0 ? (
        <div className="card-dark p-16 text-center text-white/50" data-testid="catalog-empty">
          {products.length === 0
            ? 'No products yet — click "Sync from SinaLite" above.'
            : "No products match the current filters."}
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          {/* Bulk action bar — only visible when items are selected */}
          {selectionCount > 0 && (
            <div
              className="flex flex-wrap items-center gap-2 px-5 py-3 bg-gold/10 border-b border-gold/30 text-[11px]"
              data-testid="bulk-action-bar"
            >
              <span className="uppercase tracking-[0.2em] text-gold font-medium mr-2" data-testid="bulk-selection-count">
                {selectionCount} selected
              </span>
              <button
                type="button"
                onClick={() => runBulk("visibility", true, { successMsg: "Enabled" })}
                disabled={bulkBusy}
                data-testid="bulk-show"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-emerald-400/40 text-emerald-300 hover:bg-emerald-300 hover:text-black disabled:opacity-50"
              >
                <Eye size={12} /> Show
              </button>
              <button
                type="button"
                onClick={() => runBulk("visibility", false, { successMsg: "Hidden" })}
                disabled={bulkBusy}
                data-testid="bulk-hide"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-white/20 text-white/70 hover:bg-white/10 disabled:opacity-50"
              >
                <EyeOff size={12} /> Hide
              </button>
              <button
                type="button"
                onClick={() => setBulkMarkupOpen(true)}
                disabled={bulkBusy}
                data-testid="bulk-markup-open"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-gold/50 text-gold hover:bg-gold hover:text-black disabled:opacity-50"
              >
                <Percent size={12} /> Set markup
              </button>
              <button
                type="button"
                onClick={() => runBulk("home_featured", true, { successMsg: "Featured" })}
                disabled={bulkBusy}
                data-testid="bulk-feature"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-gold/50 text-gold hover:bg-gold hover:text-black disabled:opacity-50"
              >
                <Star size={12} /> Feature
              </button>
              <button
                type="button"
                onClick={() => runBulk("home_featured", false, { successMsg: "Unfeatured" })}
                disabled={bulkBusy}
                data-testid="bulk-unfeature"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-white/20 text-white/70 hover:bg-white/10 disabled:opacity-50"
              >
                <Star size={12} /> Unfeature
              </button>
              <button
                type="button"
                onClick={() => setBulkCategoryOpen(true)}
                disabled={bulkBusy}
                data-testid="bulk-category-open"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-purple-hi/50 text-purple-hi hover:bg-purple-hi hover:text-black disabled:opacity-50"
              >
                <Tag size={12} /> Move category
              </button>
              <button
                type="button"
                onClick={() => runBulk("delete", null, {
                  confirmMsg: `Permanently delete ${selectionCount} product${selectionCount === 1 ? "" : "s"} from the local catalog? Photos will be removed. A re-sync from SinaLite will re-add them (without your overrides).`,
                  successMsg: "Deleted",
                })}
                disabled={bulkBusy}
                data-testid="bulk-delete"
                className="btn-ghost !py-2 !px-3 !text-[10px] border-red-400/40 text-red-300 hover:bg-red-500 hover:text-white disabled:opacity-50"
              >
                <Trash2 size={12} /> Delete
              </button>
              <button
                type="button"
                onClick={clearSelection}
                data-testid="bulk-clear"
                className="ml-auto text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white px-2 py-1"
              >
                Clear
              </button>
              {bulkBusy && <Loader2 size={14} className="animate-spin text-gold" />}
            </div>
          )}

          <div
            className="hidden md:grid gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/5 items-center"
            style={{ gridTemplateColumns: CATALOG_GRID }}
          >
            <div className="flex items-center justify-center">
              <BulkCheckbox
                checked={allFilteredSelected}
                indeterminate={!allFilteredSelected && someFilteredSelected}
                onChange={toggleAllFiltered}
                testId="bulk-select-all"
                title={allFilteredSelected ? "Deselect all filtered products" : "Select all filtered products"}
              />
            </div>
            <div>Photo</div>
            <div>Product</div>
            <div>Category</div>
            <div>Markup</div>
            <div className="text-center">Visible</div>
            <div className="text-right pr-1">Actions</div>
          </div>
          <ul>
            {filtered.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                selected={selectedIds.has(p.id)}
                onToggleSelect={() => toggleOne(p.id)}
                onToggle={(v) => toggleVisibility(p.id, v)}
                onSaveMarkup={(t, v) => saveMarkup(p.id, t, v)}
                onRefreshOptions={() => refreshOptions(p.id)}
                onUploadPhoto={(file) => uploadPhoto(p.id, file)}
                onRemovePhoto={() => removePhoto(p.id)}
                onEditContent={() => setEditingContent(p)}
                onScrape={() => scrapeOne(p.id)}
                onReview={() => reviewOne(p.id)}
                onGenerateCopy={() => generateCopyOne(p.id)}
                onToggleFeatured={() => toggleHomeFeatured(p.id, !p.home_featured)}
                onSaveScrapeUrl={(url) => saveScrapeUrl(p.id, url)}
              />
            ))}
          </ul>
        </div>
      )}

      {editingContent && (
        <EditContentModal
          product={editingContent}
          onClose={() => setEditingContent(null)}
          onSave={saveContent}
        />
      )}

      {bulkMarkupOpen && (
        <BulkMarkupModal
          count={selectionCount}
          onClose={() => setBulkMarkupOpen(false)}
          onApply={async (markup_type, markup_value) => {
            await runBulk("markup", { markup_type, markup_value: Number(markup_value) }, { successMsg: "Markup applied" });
            setBulkMarkupOpen(false);
          }}
        />
      )}

      {bulkCategoryOpen && (
        <BulkCategoryModal
          count={selectionCount}
          categories={categories}
          onClose={() => setBulkCategoryOpen(false)}
          onApply={async (category) => {
            await runBulk("category", category, { successMsg: `Moved to "${category}"` });
            setBulkCategoryOpen(false);
          }}
        />
      )}
    </div>
  );
}

function UrlOverridePopover({ product, onClose, onSave }) {
  const [url, setUrl] = useState(product.scrape_url_override || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onSave(url.trim()); onClose(); } catch { /* toast handled */ }
    finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-full mt-2 z-50 card-dark p-4 w-[380px] shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={`scrape-url-popover-${product.id}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Scrape URL override</div>
        <p className="text-white/60 text-xs leading-relaxed mb-3">
          If the auto-matcher can&apos;t find <span className="text-white/85">{product.name}</span>, paste the SinaLite product URL here and the scraper will use it directly.
        </p>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid={`scrape-url-input-${product.id}`}
          placeholder="https://sinalite.com/en_ca/print-products/..."
          className="w-full bg-ink-800 border border-white/10 text-white px-3 py-2 text-xs rounded-sm outline-none focus:border-purple-hi font-mono"
        />
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={submit} disabled={saving} data-testid={`scrape-url-save-${product.id}`} className="btn-gold !py-2 !px-3 !text-[10px] flex-1 justify-center disabled:opacity-40">
            {saving ? "…" : url.trim() ? "Save URL" : "Clear override"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-3 !text-[10px]">Cancel</button>
        </div>
      </div>
    </>
  );
}


function ProductRow({ product, selected, onToggleSelect, onToggle, onSaveMarkup, onRefreshOptions, onUploadPhoto, onRemovePhoto, onEditContent, onScrape, onReview, onGenerateCopy, onToggleFeatured, onSaveScrapeUrl }) {
  const [markupType, setMarkupType] = useState(product.markup_type || "percent");
  const [markupValue, setMarkupValue] = useState(String(product.markup_value ?? 100));
  const [refreshing, setRefreshing] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [showUrlEditor, setShowUrlEditor] = useState(false);
  const fileInputRef = useRef(null);
  const originalMarkup = `${product.markup_type}:${product.markup_value}`;
  const currentMarkup = `${markupType}:${markupValue}`;
  const dirty = originalMarkup !== currentMarkup;

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefreshOptions();
    setRefreshing(false);
  };

  const photoSrc = product.owner_photo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${product.owner_photo_url}?v=${product.owner_photo_uploaded_at || ""}`
    : null;

  return (
    <li
      className={`grid gap-3 px-5 py-4 border-b border-white/5 items-center transition-colors ${selected ? "bg-gold/[0.06] hover:bg-gold/[0.09]" : "hover:bg-white/[0.015]"}`}
      style={{ gridTemplateColumns: CATALOG_GRID }}
      data-testid={`catalog-row-${product.id}`}
    >
      {/* Selection checkbox */}
      <div className="flex items-center justify-center">
        <BulkCheckbox
          checked={selected}
          onChange={onToggleSelect}
          testId={`row-select-${product.id}`}
          title={selected ? "Deselect this product" : "Select this product"}
        />
      </div>
      {/* Photo cell — click to upload; hover to remove */}
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid={`photo-input-${product.id}`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUploadPhoto(f); e.target.value = ""; } }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid={`photo-upload-${product.id}`}
          title={product.has_owner_photo ? "Replace photo" : "Upload custom photo"}
          className={`w-14 h-14 border rounded-sm overflow-hidden flex items-center justify-center transition-colors relative group ${product.has_owner_photo ? "border-gold/50" : "border-dashed border-white/15 hover:border-gold"}`}
        >
          {photoSrc ? (
            <img src={photoSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <Camera size={16} className="text-white/40 group-hover:text-gold transition-colors" />
          )}
        </button>
        {product.has_owner_photo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemovePhoto(); }}
            data-testid={`photo-remove-${product.id}`}
            title="Remove custom photo"
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-400 text-white rounded-full flex items-center justify-center shadow-md"
          >
            <Trash2 size={10} />
          </button>
        )}
        {product.photo_review_status && (
          <ReviewBadge
            status={product.photo_review_status}
            confidence={product.photo_review_confidence}
            reason={product.photo_review_reason}
            productId={product.id}
          />
        )}
      </div>

      <div className="min-w-0">
        <div className="text-white text-sm truncate" title={product.name}>{product.name}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
          #{product.id} · {product.sku}
        </div>
      </div>
      <div className="text-white/60 text-xs uppercase tracking-[0.1em] truncate" title={product.category}>{product.category}</div>
      <div className="flex items-center gap-1">
        <select
          data-testid={`markup-type-${product.id}`}
          value={markupType}
          onChange={(e) => setMarkupType(e.target.value)}
          className="bg-ink-800 border border-white/10 text-white text-xs py-2 px-2 rounded-sm outline-none focus:border-gold w-16"
        >
          <option value="percent">%</option>
          <option value="fixed">$</option>
        </select>
        <div className="relative flex-1">
          {markupType === "percent" ? <Percent size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40" /> : <DollarSign size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />}
          <input
            data-testid={`markup-value-${product.id}`}
            type="number"
            step="0.01"
            min="0"
            value={markupValue}
            onChange={(e) => setMarkupValue(e.target.value)}
            className={`w-full bg-ink-800 border text-white text-xs py-2 rounded-sm outline-none focus:border-gold ${dirty ? "border-gold" : "border-white/10"} ${markupType === "percent" ? "pl-2 pr-6" : "pl-6 pr-2"}`}
          />
        </div>
        {dirty && (
          <button
            onClick={() => onSaveMarkup(markupType, markupValue)}
            data-testid={`markup-save-${product.id}`}
            title="Save markup"
            className="p-2 border border-gold text-gold hover:bg-gold hover:text-black transition-colors rounded-sm"
          ><Save size={12} /></button>
        )}
      </div>
      <div className="flex justify-center">
        <button
          onClick={() => onToggle(!product.visible)}
          data-testid={`visibility-toggle-${product.id}`}
          className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors ${product.visible ? "bg-gold" : "bg-white/10"}`}
          aria-label={product.visible ? "Hide from shop" : "Show on shop"}
        >
          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${product.visible ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="flex justify-end gap-1">
        <button
          onClick={onToggleFeatured}
          data-testid={`home-featured-${product.id}`}
          title={product.home_featured ? "Featured on Home page — click to unstar" : "Feature on Home page"}
          className={`p-2 rounded-sm transition-colors ${product.home_featured ? "text-gold hover:text-white" : "text-white/30 hover:text-gold"}`}
        >
          <Star size={13} fill={product.home_featured ? "currentColor" : "none"} strokeWidth={product.home_featured ? 1.5 : 2} />
        </button>
        <div className="relative">
          <button
            onClick={() => setShowUrlEditor((v) => !v)}
            data-testid={`scrape-url-${product.id}`}
            title={product.scrape_url_override ? "Edit manual scrape URL" : "Set a manual SinaLite URL to force-match scraping"}
            className={`p-2 border rounded-sm transition-colors ${product.scrape_url_override ? "border-purple-hi/50 text-purple-hi" : "border-white/10 text-white/40 hover:text-purple-hi hover:border-purple-hi/50"}`}
          >
            <LinkIcon size={12} />
          </button>
          {showUrlEditor && (
            <UrlOverridePopover
              product={product}
              onClose={() => setShowUrlEditor(false)}
              onSave={onSaveScrapeUrl}
            />
          )}
        </div>
        <button
          onClick={async () => { setScraping(true); try { await onScrape(); } finally { setScraping(false); } }}
          disabled={scraping || (product.has_owner_photo && (product.owner_description || "").trim())}
          data-testid={`scrape-product-${product.id}`}
          title={product.has_owner_photo && (product.owner_description || "").trim() ? "Nothing to fill — has photo + description" : "Auto-fill from SinaLite (fetches photo & description)"}
          className="p-2 border border-purple-hi/40 text-purple-hi hover:bg-purple-hi hover:text-black transition-colors rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {scraping ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
        </button>
        <button
          onClick={async () => { setReviewing(true); try { await onReview(); } finally { setReviewing(false); } }}
          disabled={reviewing || !product.has_owner_photo}
          data-testid={`review-photo-${product.id}`}
          title={!product.has_owner_photo ? "Upload a photo first to run AI review" : "Run AI vision review on this photo"}
          className="p-2 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {reviewing ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
        </button>
        <button
          onClick={async () => { setGeneratingCopy(true); try { await onGenerateCopy(); } finally { setGeneratingCopy(false); } }}
          disabled={generatingCopy || !product.has_owner_photo}
          data-testid={`generate-copy-${product.id}`}
          title={!product.has_owner_photo ? "Upload a photo first — copy generation is vision-based" : "Generate SEO alt-text + short description with AI"}
          className="p-2 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-300 hover:text-black transition-colors rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {generatingCopy ? <Loader2 size={12} className="animate-spin" /> : <PenSquare size={12} />}
        </button>
        <button
          onClick={onEditContent}
          data-testid={`edit-content-${product.id}`}
          title={(product.owner_description || product.owner_tagline) ? "Edit tagline & description" : "Write custom tagline & description (currently using auto-generated)"}
          className={`p-2 border rounded-sm transition-colors ${(product.owner_description || product.owner_tagline) ? "border-gold/50 text-gold hover:bg-gold hover:text-black" : "border-white/10 text-white/60 hover:text-gold hover:border-gold"}`}
        >
          <FileText size={12} />
        </button>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid={`refresh-options-${product.id}`}
          title="Re-fetch option groups from SinaLite"
          className="p-2 border border-white/10 text-white/60 hover:text-gold hover:border-gold transition-colors rounded-sm disabled:opacity-50"
        >
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />}
        </button>
        <a
          href={`/shop/product/${product.id}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`preview-product-${product.id}`}
          title="Preview on storefront"
          className={`p-2 border rounded-sm transition-colors ${product.visible ? "border-white/10 text-white/60 hover:text-gold hover:border-gold" : "border-white/5 text-white/20 pointer-events-none"}`}
        >
          {product.visible ? <Eye size={12} /> : <EyeOff size={12} />}
        </a>
      </div>
    </li>
  );
}


function EditContentModal({ product, onClose, onSave }) {
  const [tagline, setTagline] = useState(product.owner_tagline || "");
  const [description, setDescription] = useState(product.owner_description || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(product.id, { owner_tagline: tagline, owner_description: description });
      onClose();
    } catch { /* toast handled by parent */ }
    finally { setSaving(false); }
  };

  const clearOverride = async () => {
    if (!window.confirm("Remove your custom copy and fall back to the auto-generated description?")) return;
    setSaving(true);
    try {
      await onSave(product.id, { owner_tagline: "", owner_description: "" });
      onClose();
    } catch { /* noop */ }
    finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-content-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-xl card-dark p-8 relative"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white"
          aria-label="Close"
          data-testid="edit-content-close"
        ><X size={16} /></button>

        <div className="eyebrow gold-bar">Catalog</div>
        <h2 className="font-display text-2xl mt-2 tracking-tight">Edit copy</h2>
        <p className="text-white/50 text-xs mt-2 truncate" title={product.name}>#{product.id} · {product.name}</p>

        <label className="block mt-6">
          <span className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">
            <span>Tagline <span className="text-white/30 normal-case">(short subhead — appears under the product name)</span></span>
            <span className="text-white/30">{tagline.length}/140</span>
          </span>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value.slice(0, 140))}
            data-testid="edit-content-tagline"
            placeholder={`e.g. "Premium ${product.category?.toLowerCase() || "print"} — expertly produced"`}
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
          />
        </label>

        <label className="block mt-4">
          <span className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">
            <span>Description <span className="text-white/30 normal-case">(shown on the product page)</span></span>
            <span className="text-white/30">{description.length}/2000</span>
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
            data-testid="edit-content-description"
            rows={6}
            placeholder={`Describe what makes this ${product.category?.toLowerCase() || "product"} great — materials, use cases, sizing options…`}
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold leading-relaxed resize-y"
          />
        </label>

        <div className="mt-7 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-content-save"
            className="btn-gold flex-1 justify-center disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          {(product.owner_tagline || product.owner_description) && (
            <button
              type="button"
              onClick={clearOverride}
              disabled={saving}
              data-testid="edit-content-clear"
              className="text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
            >Reset to auto</button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost !py-3 !px-4"
          >Cancel</button>
        </div>

        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-5">
          Blank fields fall back to the auto-generated / category default copy.
        </p>
      </form>
    </div>
  );
}

function ReviewBadge({ status, confidence, reason, productId }) {
  const cfg = {
    ok:      { Icon: ShieldCheck,    cls: "bg-emerald-500 text-black",  label: "OK" },
    unsure:  { Icon: ShieldQuestion, cls: "bg-amber-400 text-black",    label: "?" },
    flagged: { Icon: ShieldAlert,    cls: "bg-red-500 text-white",      label: "!" },
  }[status];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  const pct = confidence != null ? Math.round(confidence * 100) : null;
  const tip = `AI verdict: ${status.toUpperCase()}${pct != null ? ` (${pct}% confidence)` : ""}${reason ? " — " + reason : ""}`;
  return (
    <div
      className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center shadow-md ${cfg.cls}`}
      title={tip}
      data-testid={`review-badge-${productId}`}
      data-review-status={status}
    >
      <Icon size={10} strokeWidth={2.5} />
    </div>
  );
}


function BulkCheckbox({ checked, indeterminate, onChange, testId, title }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate && !checked);
  }, [indeterminate, checked]);
  return (
    <label
      className="relative flex items-center justify-center cursor-pointer group w-6 h-6"
      title={title}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        data-testid={testId}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <span
        className={`w-4 h-4 rounded-sm border flex items-center justify-center transition-colors pointer-events-none ${
          checked
            ? "bg-gold border-gold text-black"
            : indeterminate
              ? "bg-gold/40 border-gold text-black"
              : "border-white/30 group-hover:border-gold"
        }`}
      >
        {checked ? (
          <Check size={11} strokeWidth={3} />
        ) : indeterminate ? (
          <span className="block w-2 h-[2px] bg-black" />
        ) : null}
      </span>
    </label>
  );
}

function BulkMarkupModal({ count, onClose, onApply }) {
  const [markupType, setMarkupType] = useState("percent");
  const [markupValue, setMarkupValue] = useState("100");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (markupValue === "" || Number.isNaN(Number(markupValue))) return;
    setSaving(true);
    try { await onApply(markupType, markupValue); } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-markup-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md card-dark p-8 relative"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" aria-label="Close">
          <X size={16} />
        </button>
        <div className="eyebrow gold-bar">Bulk edit</div>
        <h2 className="font-display text-2xl mt-2 tracking-tight">Set markup</h2>
        <p className="text-white/50 text-xs mt-2">
          Overwrites the markup on <span className="text-white">{count} selected product{count === 1 ? "" : "s"}</span>.
        </p>

        <div className="flex gap-2 mt-6">
          <select
            data-testid="bulk-markup-type"
            value={markupType}
            onChange={(e) => setMarkupType(e.target.value)}
            className="bg-ink-800 border border-white/10 text-white text-sm py-3 px-3 rounded-sm outline-none focus:border-gold w-24"
          >
            <option value="percent">Percent</option>
            <option value="fixed">Fixed $</option>
          </select>
          <div className="relative flex-1">
            {markupType === "percent"
              ? <Percent size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
              : <DollarSign size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />}
            <input
              data-testid="bulk-markup-value"
              type="number"
              step="0.01"
              min="0"
              value={markupValue}
              onChange={(e) => setMarkupValue(e.target.value)}
              autoFocus
              className={`w-full bg-ink-800 border border-white/10 text-white text-sm py-3 rounded-sm outline-none focus:border-gold ${markupType === "percent" ? "pl-3 pr-9" : "pl-8 pr-3"}`}
            />
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-4">
          Example: 100% doubles the wholesale price · $5 adds $5 flat per unit.
        </p>

        <div className="mt-6 flex gap-3">
          <button type="submit" disabled={saving} data-testid="bulk-markup-apply" className="btn-gold flex-1 justify-center disabled:opacity-40">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : `Apply to ${count}`}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost !py-3 !px-4">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function BulkCategoryModal({ count, categories, onClose, onApply }) {
  const [mode, setMode] = useState("existing"); // existing | custom
  const [existing, setExisting] = useState(categories[0] || "");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = (mode === "existing" ? existing : custom).trim();
    if (!value) return;
    setSaving(true);
    try { await onApply(value); } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-category-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md card-dark p-8 relative"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" aria-label="Close">
          <X size={16} />
        </button>
        <div className="eyebrow gold-bar">Bulk edit</div>
        <h2 className="font-display text-2xl mt-2 tracking-tight">Move to category</h2>
        <p className="text-white/50 text-xs mt-2">
          Reassigns <span className="text-white">{count} selected product{count === 1 ? "" : "s"}</span> to a new category. Only affects local storefront grouping — SinaLite&apos;s own category stays the same on their side.
        </p>

        <div className="flex border border-white/10 rounded-sm overflow-hidden text-[10px] uppercase tracking-[0.18em] mt-5">
          {["existing", "custom"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`bulk-cat-mode-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 px-3 py-2 transition-colors ${mode === m ? "bg-gold text-black" : "text-white/55 hover:text-gold"}`}
            >{m === "existing" ? "Existing" : "New"}</button>
          ))}
        </div>

        {mode === "existing" ? (
          <select
            data-testid="bulk-category-existing"
            value={existing}
            onChange={(e) => setExisting(e.target.value)}
            className="mt-3 w-full bg-ink-800 border border-white/10 text-white text-sm py-3 px-3 rounded-sm outline-none focus:border-gold"
          >
            {categories.length === 0 && <option value="">No categories yet</option>}
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <input
            type="text"
            data-testid="bulk-category-custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. Storefront Signage"
            autoFocus
            className="mt-3 w-full bg-ink-800 border border-white/10 text-white text-sm py-3 px-3 rounded-sm outline-none focus:border-gold"
          />
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={saving || (mode === "existing" ? !existing : !custom.trim())}
            data-testid="bulk-category-apply"
            className="btn-gold flex-1 justify-center disabled:opacity-40"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Moving…</> : `Move ${count} to category`}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost !py-3 !px-4">Cancel</button>
        </div>
      </form>
    </div>
  );
}
